import { Box } from '@mui/system';
import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { ClinicalReviewActivityItemProps } from './InitialReviewActivity';
import { regularTypographyStyle, smallerTypographyStyle } from '../../../style';
import { SmallerTextBox } from '../../../styledComponents';
import crmNewLineReplacer from '../../../lib/helper/crm-new-line-replacer';
import formatNewLines from '../../../lib/helper/format-new-lines';

const AppealReviewActivity = ({ item, mediaSm }: ClinicalReviewActivityItemProps) => {
    const [expandedPanels, setExpandedPanels] = React.useState<string[]>([
        item.nurseActivity,
        item.physicianActivity,
        item.nurseDecision
    ]);

    const handleChange = (isExpanded: boolean, panel: string) => {
        isExpanded
            ? setExpandedPanels((expandedPanels: string[]) => [
                  ...expandedPanels,
                  isExpanded ? panel : ''
              ])
            : setExpandedPanels((expandedPanels: string[]) =>
                  expandedPanels.filter((n: string) =>
                      typeof n === 'string' ? !n.startsWith(panel) : n
                  )
              );
    };
    const nurseConclutions = item.nurseActivity
        ? item.nurseActivity.replace('Comments', 'Conclusions')
        : null;

    return (
        <>
            {item.nurseConclusions || item.comorbidityIndexScore ? (
                <Accordion
                    sx={
                        !mediaSm
                            ? {
                                  boxShadow: '0',
                                  '&:before': { display: 'none' }
                              }
                            : {
                                  padding: '0 0.75rem',
                                  boxShadow: '0',
                                  '&:before': { display: 'none' }
                              }
                    }
                    expanded={
                        expandedPanels instanceof Array
                            ? expandedPanels.includes(item.nurseActivity)
                            : false
                    }
                    elevation={0}
                    disableGutters={true}
                    onChange={(_: unknown, isExpanded: boolean) =>
                        handleChange(isExpanded, item.nurseActivity)
                    }>
                    <AccordionSummary
                        expandIcon={
                            expandedPanels.includes(item.nurseActivity) ? (
                                <RemoveIcon />
                            ) : (
                                <AddIcon />
                            )
                        }
                        sx={{ padding: '0' }}>
                        <Box>
                            <Typography sx={regularTypographyStyle}>
                                {item.nurseActivity}
                            </Typography>
                            <Typography sx={smallerTypographyStyle}>
                                Reviewed By {item.nurseName}
                            </Typography>
                        </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ padding: '0', marginBottom: '0.625rem' }}>
                        <Box sx={{ fontSize: '0.813rem', whiteSpace: 'pre-line' }}>
                            <SmallerTextBox>{formatNewLines(item.nurseConclusions)}</SmallerTextBox>
                            <SmallerTextBox>
                                <b>Comorbidity Index Score: {item.comorbidityIndexScore}</b>
                            </SmallerTextBox>
                            {item.prosthetics && (
                                <SmallerTextBox>
                                    Implant Information (Prosthetics): {item.prosthetics}
                                </SmallerTextBox>
                            )}
                            {item.medicalDevices && (
                                <SmallerTextBox>
                                    Implant Information (Medical Devices): {item.medicalDevices}
                                </SmallerTextBox>
                            )}
                            {item.instrumentation && (
                                <SmallerTextBox>
                                    Implant Information (Instrumentation): {item.instrumentation}
                                </SmallerTextBox>
                            )}
                            {item.grafts && (
                                <SmallerTextBox>
                                    Implant Information (Grafts): {item.grafts}
                                </SmallerTextBox>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            ) : null}

            {item.nurseDecision ? (
                <Accordion
                    sx={
                        !mediaSm
                            ? {
                                  boxShadow: '0',
                                  '&:before': { display: 'none' }
                              }
                            : {
                                  padding: '0 0.75rem',
                                  boxShadow: '0',
                                  '&:before': { display: 'none' }
                              }
                    }
                    expanded={
                        expandedPanels instanceof Array
                            ? expandedPanels.includes(item.nurseDecision)
                            : false
                    }
                    elevation={0}
                    disableGutters={true}
                    onChange={(_: unknown, isExpanded: boolean) =>
                        handleChange(isExpanded, item.nurseDecision)
                    }>
                    <AccordionSummary
                        expandIcon={
                            expandedPanels.includes(item.nurseDecision) ? (
                                <RemoveIcon />
                            ) : (
                                <AddIcon />
                            )
                        }
                        sx={{ padding: '0' }}>
                        <Box>
                            <Typography sx={regularTypographyStyle}>{nurseConclutions}</Typography>
                            <Typography sx={smallerTypographyStyle}>
                                Reviewed By {item.nurseName}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: '0', marginBottom: '0.625rem' }}>
                        <SmallerTextBox>{item.nurseDecision}</SmallerTextBox>
                    </AccordionDetails>
                </Accordion>
            ) : null}

            {item.physicianName ? (
                <Accordion
                    sx={
                        !mediaSm
                            ? {
                                  boxShadow: '0',
                                  '&:before': { display: 'none' }
                              }
                            : {
                                  padding: '0 0.75rem',
                                  boxShadow: '0',
                                  '&:before': { display: 'none' }
                              }
                    }
                    expanded={
                        expandedPanels instanceof Array
                            ? expandedPanels.includes(item.physicianActivity)
                            : false
                    }
                    elevation={0}
                    disableGutters={true}
                    onChange={(_: unknown, isExpanded: boolean) =>
                        handleChange(isExpanded, item.physicianActivity)
                    }>
                    <AccordionSummary
                        expandIcon={
                            expandedPanels.includes(item.physicianActivity) ? (
                                <RemoveIcon />
                            ) : (
                                <AddIcon />
                            )
                        }
                        sx={{ padding: '0' }}>
                        <Box>
                            <Typography sx={regularTypographyStyle}>
                                {item.physicianActivity}
                            </Typography>
                            {/* <Typography sx={smallerTypographyStyle}>
                                Reviewed By {item.physicianName}
                            </Typography> */}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: '0', marginBottom: '0.625rem' }}>
                        <SmallerTextBox>{crmNewLineReplacer(item.physicianReview)}</SmallerTextBox>
                        {item.isAppAppropriateValue && (
                            <SmallerTextBox>
                                APP Appropriate: {item.isAppAppropriateValue}
                            </SmallerTextBox>
                        )}
                        {item.spanishPhysicianReview ? (
                            <>
                                <Box>
                                    <Typography sx={regularTypographyStyle}>
                                        {item.physicianActivity} (Spanish)
                                    </Typography>
                                </Box>
                                <SmallerTextBox>
                                    {crmNewLineReplacer(item.spanishPhysicianReview)}
                                </SmallerTextBox>
                            </>
                        ) : null}
                        {item.physicianDecision ? (
                            <SmallerTextBox>
                                <Typography
                                    sx={{
                                        fontWeight: 'bold',
                                        display: 'inline',
                                        fontSize: '0.813rem'
                                    }}>
                                    Medical Review Determination:{' '}
                                </Typography>
                                {item.physicianDecision}
                            </SmallerTextBox>
                        ) : null}
                    </AccordionDetails>
                </Accordion>
            ) : null}
        </>
    );
};

export default AppealReviewActivity;
